'use client';
import React, { useState, useEffect } from 'react';

interface FormData {
  fullName: string;
  email: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  message?: string;
}

interface SiteInfo {
  phone: string;
  location: string;
  email: string;
}

function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({ fullName: '', email: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [siteInfo, setSiteInfo] = useState<SiteInfo>({
    phone: '+628-23-9092-4636',
    location: 'Indonesia - Rawajati/Kalibata, Kec. Pancoran, Kota Jakarta Selatan, DKI JAKARTA',
    email: 'fajrul@fajrul.id',
  });

  useEffect(() => {
    fetch('/api/settings')
      .then((res) => res.json())
      .then((data) => {
        if (data?.settings) {
          setSiteInfo({
            phone: data.settings.phone,
            location: data.settings.location,
            email: data.settings.email,
          });
        }
      })
      .catch(() => {});
  }, []);

  const validate = (data: FormData): FormErrors => {
    const errs: FormErrors = {};
    if (!data.fullName.trim()) errs.fullName = 'Full name is required.';
    if (!data.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!validateEmail(data.email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!data.message.trim()) errs.message = 'Message is required.';
    return errs;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (touched[field]) {
      const errs = validate(updated);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(formData);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = { fullName: true, email: true, message: true };
    setTouched(allTouched);
    const errs = validate(formData);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitStatus('submitting');
    try {
      const res = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', message: '' });
        setTouched({});
        setErrors({});
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const inputClass = (field: keyof FormData) =>
    `w-full bg-gray-300 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline ${
      touched[field] && errors[field] ? 'ring-2 ring-red-400' : ''
    }`;

  return (
    <section id="contact" className="p-4 sm:p-6 lg:p-8 text-[#48438e]">
      <div className="max-w-screen-xl mt-24 px-8 grid gap-8 grid-cols-1 md:grid-cols-2 md:px-12 lg:px-16 xl:px-32 py-16 mx-auto bg-white text-gray-900 rounded-lg shadow-lg">
        <div className="flex flex-col justify-between">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">Lets talk about everything!</h2>
            <div className="mt-8 flex-col gap-4">
              <p>Text/Call Me : <strong>{siteInfo.phone}</strong></p>
              <p>Location :<strong> {siteInfo.location}</strong></p>
            </div>
            <div className="text-gray-700 mt-8">
              Hate forms? Send us an{' '}
              <span className="underline font-semibold">
                <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>
              </span>{' '}
              instead.
            </div>
          </div>
        </div>

        <div>
          {/* Success / Error Banner */}
          {submitStatus === 'success' && (
            <div className="mb-6 flex items-center gap-3 bg-green-50 border border-green-300 text-green-700 rounded-lg px-4 py-3 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Your message has been sent successfully! I'll get back to you soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Something went wrong. Please try again or email me directly.
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="fn" className="uppercase text-sm text-gray-600 font-bold">Full Name</label>
              <input
                id="fn"
                className={inputClass('fullName')}
                type="text"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                onBlur={() => handleBlur('fullName')}
              />
              {touched.fullName && errors.fullName && (
                <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>
              )}
            </div>
            <div className="mt-8">
              <label htmlFor="email" className="uppercase text-sm text-gray-600 font-bold">Email</label>
              <input
                id="email"
                className={inputClass('email')}
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
              />
              {touched.email && errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            <div className="mt-8">
              <label htmlFor="mess" className="uppercase text-sm text-gray-600 font-bold">Message</label>
              <textarea
                id="mess"
                className={inputClass('message')}
                style={{ height: '8rem' }}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                onBlur={() => handleBlur('message')}
              />
              {touched.message && errors.message && (
                <p className="mt-1 text-xs text-red-500">{errors.message}</p>
              )}
            </div>
            <div className="mt-8">
              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="uppercase text-sm font-bold tracking-wide bg-[#48438e] text-gray-100 p-3 rounded-lg w-full focus:outline-none focus:shadow-outline hover:bg-[#3a3578] transition disabled:opacity-60"
              >
                {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
